import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { ArrowLeft, Check, CheckCircle, FileText, Loader } from 'lucide-react';
import DocumentViewer from '../../components/DocumentViewer';
import type { DocumentSource } from '../../components/DocumentViewer';
import FieldCard from '../../components/FieldCard';
import { ConfidencePill } from '../../components/ui/ConfidencePill';
import { EmptyState } from '../../components/ui/EmptyState';
import { RunningState } from '../../components/ui/RunningState';
import { StatusDot } from '../../components/ui/StatusDot';
import type { StatusDotTone } from '../../components/ui/StatusDot';
import { FileStatus, JobType } from '../../graphql/types';
import type { File as FileType, WorkflowJob } from '../../graphql/types';
import { RailItem } from '../Rail';
import { RailSlotContext } from '../railSlot';
import { TOP_BAR_HEIGHT_PX } from '../TopBar';
import { readWorkspaceParams, writeWorkspaceParams } from '../urlState';
import { loadFilePdf } from '../extract/extractData';
import { parsePageRange } from '../extract/extractModel';
import { hasPageLocation } from '../../components/documentViewerGeometry';
import {
  acceptAllFields,
  acceptEveryField,
  CONFIDENCE_THRESHOLD,
  countBulkAcceptable,
  countWithoutAction,
  displayValue,
  documentProgress,
  documentReviewStatus,
  setFieldAction,
  storedActionsOf,
  summarizeReview,
} from './reviewModel';
import type { DocumentReviewStatus, ReviewActions, ReviewDocument, ReviewField } from './reviewModel';
import { useReviewDocuments } from './useReviewDocuments';

/**
 * ReviewMode (Title Search): recorded-document list, Document_Viewer and the
 * Field_Cards of the selected document, with review progress and the Finalize
 * control at the foot of the field list.
 *
 * Laid out like Extract mode (BA): the document list is in the Workspace Rail
 * (through the Rail slot; beside the viewer when rendered on its own), then the
 * Document_Viewer (flexible), then a 400px field list whose footer holds the
 * progress and the primary action.
 *
 * - Shown for every unfinalized Title Search, not only once the job reports all
 *   loans processed. Until a document arrives it shows `emptyState`, and
 *   Finalize stays off while any file is still processing.
 *
 * - Loading, parsing and the document subscription are those of the legacy
 *   `RecordedDocumentReviewStep` (`useReviewDocuments`, `parseReviewFields`,
 *   `CONFIDENCE_THRESHOLD`).
 * - Each Field_Card offers Accept and Edit (inline input with Save and
 *   Cancel). There is no Reject: the reviewer enters the correct value instead
 *   (Requirements 8.3, 8.4). Rejections stored by earlier reviews still show
 *   in the read-only view.
 * - A document counts as reviewed only when every field has an action, and
 *   Finalize unlocks only when every document is reviewed (8.6, 8.7), both
 *   through `titleSearch/review.ts`.
 * - Selection (`doc`, `field`) lives in the Workspace URL params.
 *
 * Finalize is a callback: this component reports the documents and the
 * reviewer's actions through `onFinalize` and shows `finalizing` and
 * `finalizeError`; persisting them is the caller's job.
 *
 * Requirements: 8.1, 8.2, 8.3, 8.4, 8.7.
 */
export interface ReviewFinalizeInput {
  documents: ReviewDocument[];
  /** Source files by id, so the caller can address each file when saving. */
  files: Record<string, FileType>;
  actions: ReviewActions;
}

export interface ReviewModeProps {
  job: Pick<WorkflowJob, 'jobId' | 'uploadLocation'>;
  /** S3 bucket of the uploaded documents. Defaults to the job's upload location. */
  uploadBucket?: string;
  /** Called when the reviewer activates Finalize (only possible when enabled). */
  onFinalize?: (input: ReviewFinalizeInput) => void | Promise<void>;
  /** True while the caller is persisting the review. */
  finalizing?: boolean;
  /** Plain-language error from the caller's finalize attempt. */
  finalizeError?: string | null;
  /** Shown instead of the review while no document has been extracted yet, for example the processing state. */
  emptyState?: React.ReactNode;
  /**
   * Called by "Back to upload", which returns to the upload and processing
   * view. Without it there is no back button.
   */
  onBack?: () => void;
  /**
   * Read-only view only: lets the reader correct a value after the review is
   * finalized. Called with the document, its file and the stored actions
   * including the edit; it must persist them and reject on failure. Without it
   * a finalized review has no Edit.
   */
  onSaveEdit?: (input: {
    document: ReviewDocument;
    file: FileType;
    actions: ReviewActions;
  }) => Promise<void>;
  /**
   * The reviewer's actions, when the caller keeps them so they survive leaving
   * and re-entering Review mode. Without them, ReviewMode keeps its own.
   */
  actions?: ReviewActions;
  onActionsChange?: (actions: ReviewActions) => void;
  /**
   * Read-only view of a finalized review (Requirement 8.9). Each field shows
   * the action and edited value stored with the review, with no Accept or
   * Edit, no bulk accept and no Finalize. Selection, the document list,
   * the Document_Viewer and the status dots keep working.
   */
  readOnly?: boolean;
}

const DOT_TONE: Record<DocumentReviewStatus, StatusDotTone> = {
  reviewed: 'success',
  attention: 'warning',
  'in-progress': 'idle',
  'not-started': 'idle',
};

const FINALIZE_BUTTON =
  'inline-flex min-w-[150px] shrink-0 items-center justify-center gap-2 rounded-lg bg-[#834DFB] px-4 py-2 text-sm font-bold text-white transition-colors hover:bg-[#6E2EF0] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#834DFB] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-[#834DFB]';

const FINALIZED_HINT = 'This review is finalized. Actions and edited values are read-only.';
const EDIT_ERROR_MESSAGE = 'The edit could not be saved. Check your connection and try again.';
const FINALIZED_EDITABLE_HINT =
  'This review is finalized. Use Edit to correct a value; the change is saved at once.';

const THRESHOLD_PERCENT = Math.round(CONFIDENCE_THRESHOLD * 100);

function statusLabel(status: DocumentReviewStatus): string {
  switch (status) {
    case 'reviewed':
      return 'Reviewed';
    case 'attention':
      return `Has fields below ${THRESHOLD_PERCENT}% confidence`;
    case 'in-progress':
      return 'In progress';
    default:
      return 'Not started';
  }
}

function bucketOf(job: ReviewModeProps['job'], explicit?: string): string | undefined {
  if (explicit) return explicit;
  if (job.uploadLocation) return job.uploadLocation.replace(/^s3:\/\//, '').split('/')[0];
  return undefined;
}

export const ReviewMode: React.FC<ReviewModeProps> = ({
  job,
  uploadBucket,
  onFinalize,
  finalizing = false,
  finalizeError = null,
  emptyState,
  onBack,
  onSaveEdit,
  actions: controlledActions,
  onActionsChange,
  readOnly = false,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const railSlot = useContext(RailSlotContext);
  const { documents, files, loading, error, reload } = useReviewDocuments(job.jobId);

  // ── Reviewer actions, held locally until finalize ─────────────────────────
  // A finalized review shows the actions stored with the documents instead.
  const [ownActions, setOwnActions] = useState<ReviewActions>({});
  const draftActions = controlledActions ?? ownActions;
  const setActions = (update: (prev: ReviewActions) => ReviewActions) => {
    if (onActionsChange) onActionsChange(update(draftActions));
    else setOwnActions(update);
  };
  const storedActions = useMemo(() => storedActionsOf(documents), [documents]);

  // Corrections after finalize are saved one field at a time.
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const saveFinalizedEdit = async (field: ReviewField, value: string) => {
    const doc = documents.find((d) => d.documentId === field.documentId);
    const sourceFile = doc ? files[doc.fileId] : undefined;
    if (!onSaveEdit || !doc || !sourceFile) {
      setEditError(EDIT_ERROR_MESSAGE);
      return;
    }
    setSavingEdit(true);
    setEditError(null);
    try {
      await onSaveEdit({
        document: doc,
        file: sourceFile,
        actions: setFieldAction(storedActions, field.id, 'accept', value),
      });
      // Read the saved fields back so the card shows the stored value.
      await reload();
    } catch (err) {
      console.error('[ReviewMode] Failed to save the edit:', err);
      setEditError(EDIT_ERROR_MESSAGE);
    } finally {
      setSavingEdit(false);
    }
  };
  const actions = readOnly ? storedActions : draftActions;

  // ── Selection, kept in the URL ────────────────────────────────────────────
  const params = readWorkspaceParams(searchParams, JobType.TR);
  const selectedDoc = documents.find((d) => d.documentId === params.doc) ?? documents[0];
  const selectedDocId = selectedDoc?.documentId ?? null;
  const selectedField = selectedDoc?.fields.find((f) => f.id === params.field);
  const selectedFieldId = selectedField?.id ?? null;

  const [page, setPage] = useState(1);
  const lastDocRef = useRef<string | null>(null);

  const selectDocument = (doc: ReviewDocument) => {
    setPage(doc.firstPage);
    setSearchParams(writeWorkspaceParams(searchParams, { doc: doc.documentId, field: null }));
  };

  const selectField = useCallback(
    (field: ReviewField) => {
      // An empty field has no page of its own: stay where the reviewer is.
      if (hasPageLocation(field)) setPage(field.page);
      setSearchParams(
        writeWorkspaceParams(searchParams, { doc: field.documentId, field: field.id })
      );
    },
    [searchParams, setSearchParams]
  );

  // A deep link, or the first load, moves the viewer to the selection's page.
  useEffect(() => {
    const docChanged = lastDocRef.current !== selectedDocId;
    lastDocRef.current = selectedDocId;
    if (selectedField && hasPageLocation(selectedField)) setPage(selectedField.page);
    // An empty field keeps the current page, unless it opened another document.
    else if (selectedDoc && (docChanged || !selectedField)) setPage(selectedDoc.firstPage);
    // Only react to the selection changing, not to every document refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDocId, selectedFieldId]);

  const cardRefs = useRef<Record<string, HTMLDivElement | null>>({});
  useEffect(() => {
    if (selectedFieldId) cardRefs.current[selectedFieldId]?.scrollIntoView?.({ block: 'nearest' });
  }, [selectedFieldId]);

  // Every field's source is marked on the page, badged with the number the
  // field has in the list so the two can be matched at a glance.
  const highlights = useMemo(
    () =>
      (selectedDoc?.fields ?? []).flatMap((f, i) =>
        f.boundingBox
          ? [{ id: f.id, page: f.page, boundingBox: f.boundingBox, badge: String(i + 1) }]
          : []
      ),
    [selectedDoc]
  );

  // ── Document pages ────────────────────────────────────────────────────────
  const bucket = bucketOf(job, uploadBucket);
  const file = selectedDoc ? files[selectedDoc.fileId] : undefined;
  const fileId = file?.fileId;
  const pdfCache = useRef(new Map<string, ArrayBuffer>());
  const [pdfBytes, setPdfBytes] = useState<ArrayBuffer | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);

  useEffect(() => {
    setPdfError(null);
    if (!file || !fileId) {
      setPdfBytes(null);
      setPdfLoading(false);
      return;
    }
    const cached = pdfCache.current.get(fileId);
    if (cached) {
      setPdfBytes(cached);
      setPdfLoading(false);
      return;
    }
    let cancelled = false;
    setPdfBytes(null);
    setPdfLoading(true);
    loadFilePdf(file, bucket, null)
      .then((bytes) => {
        pdfCache.current.set(fileId, bytes);
        if (!cancelled) setPdfBytes(bytes);
      })
      .catch((err) => {
        console.error('[ReviewMode] Failed to load the file:', err);
        if (!cancelled) {
          setPdfError(
            'The file could not be loaded. Reload the page, or upload it again if this keeps happening.'
          );
        }
      })
      .finally(() => {
        if (!cancelled) setPdfLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // `file` changes identity on refetch; the file id decides the document.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileId, bucket]);

  const source = useMemo<DocumentSource | null>(
    () => (pdfBytes ? { data: pdfBytes } : null),
    [pdfBytes]
  );

  // ── Progress and Finalize gate ────────────────────────────────────────────
  const summary = useMemo(() => summarizeReview(documents, actions), [documents, actions]);
  const { totalDocuments, finalizeEnabled } = summary;
  // Finalize requires every document to be reviewed, so a finalized review has
  // reviewed them all, even when it predates stored actions.
  const reviewedDocuments = readOnly ? totalDocuments : summary.reviewedDocuments;
  const progressPercent =
    totalDocuments === 0 ? 0 : Math.round((reviewedDocuments / totalDocuments) * 100);
  // A file still being extracted may add documents, so Finalize waits for it.
  const processingFiles = useMemo(
    () => Object.values(files).filter((f) => f.status === FileStatus.PROCESSING).length,
    [files]
  );
  const canFinalize = finalizeEnabled && processingFiles === 0 && !finalizing;
  const finalizeBlockedReason =
    processingFiles > 0
      ? `${processingFiles} ${processingFiles === 1 ? 'file is' : 'files are'} still processing. Finalize once ${processingFiles === 1 ? 'it finishes' : 'they finish'}.`
      : 'Every field on every document needs an action before you can finalize.';
  const finalizedHint = onSaveEdit ? FINALIZED_EDITABLE_HINT : FINALIZED_HINT;
  const finalizeHint =
    processingFiles > 0
      ? finalizeBlockedReason
      : finalizeEnabled
        ? 'Every document is reviewed. Finalize to unlock Findings.'
        : 'Give every field on every document an action to finalize.';

  // Page n of m inside the selected document, as in Extract mode.
  const docPages = parsePageRange(selectedDoc?.pageRange);
  const pageCaption =
    docPages && page >= docPages.start && page <= docPages.end
      ? `Page ${page - docPages.start + 1} of ${docPages.end - docPages.start + 1}`
      : `Page ${page}`;
  const viewerCaption = selectedDoc
    ? [selectedDoc.sourceFilename, selectedDoc.documentClass, pageCaption].filter(Boolean).join(' · ')
    : pageCaption;

  const handleFinalize = () => {
    if (!canFinalize) return;
    void onFinalize?.({ documents, files, actions });
  };

  // "Accept all ≥ 80%" applies to the selected document and only fills gaps.
  const bulkCount = selectedDoc ? countBulkAcceptable(selectedDoc.fields, actions) : 0;
  const handleAcceptAll = () => {
    if (!selectedDoc || readOnly) return;
    setActions((prev) => acceptAllFields(selectedDoc.fields, prev));
  };

  // "Accept all" also fills gaps in the selected document, at any confidence.
  const openCount = selectedDoc ? countWithoutAction(selectedDoc.fields, actions) : 0;
  const handleAcceptEvery = () => {
    if (!selectedDoc || readOnly) return;
    setActions((prev) => acceptEveryField(selectedDoc.fields, prev));
  };

  // ── Document list ─────────────────────────────────────────────────────────
  // Inside a Title Search Workspace the list goes to the end of the Rail, as the
  // BA Rail lists its documents. Rendered on its own, it sits beside the viewer.
  const documentList =
    documents.length > 0 ? (
      <>
        <p className="px-4 pb-2 pt-4 text-[11px] font-bold uppercase tracking-[0.1em] text-gray-500">
          Received documents
        </p>
        {documents.map((doc) => {
          const { reviewed, total } = documentProgress(doc, actions);
          const status: DocumentReviewStatus = readOnly
            ? 'reviewed'
            : documentReviewStatus(doc, actions);
          return (
            <RailItem
              key={doc.documentId}
              selected={doc.documentId === selectedDocId}
              onClick={() => selectDocument(doc)}
            >
              <span data-testid="review-doc-item" data-status={status} className="block">
                <span className="flex items-start justify-between gap-2">
                  <span className="block font-bold text-gray-900">{doc.documentClass}</span>
                  <span className="mt-1.5">
                    <StatusDot tone={DOT_TONE[status]} label={statusLabel(status)} />
                  </span>
                </span>
                {doc.confidence !== null ? (
                  <span className="mt-1 block" data-testid="review-doc-confidence">
                    <ConfidencePill score={doc.confidence} showPercent />
                  </span>
                ) : null}
                <span className="mt-1 block text-xs text-gray-500">
                  {doc.instrumentNumber
                    ? `Instrument ${doc.instrumentNumber}`
                    : 'No instrument number'}
                </span>
                <span className="block text-xs text-gray-500">
                  {readOnly
                    ? `${total} ${total === 1 ? 'field' : 'fields'}`
                    : `${reviewed} of ${total} fields reviewed`}
                </span>
                {doc.sourceFilename ? (
                  <span
                    data-testid="review-doc-file"
                    title={doc.sourceFilename}
                    className="mt-0.5 block truncate text-xs text-gray-500"
                  >
                    {doc.sourceFilename}
                  </span>
                ) : null}
              </span>
            </RailItem>
          );
        })}
      </>
    ) : null;

  const listInRail = railSlot !== undefined;

  // ── Body ──────────────────────────────────────────────────────────────────
  let stateBody: React.ReactNode = null;
  if (loading && documents.length === 0) {
    stateBody = (
      <div className="m-auto w-full max-w-sm p-6">
        <RunningState
          title="Loading received documents"
          description="Fetching the documents and their extracted fields. This takes a moment."
        />
      </div>
    );
  } else if (error && documents.length === 0) {
    stateBody = (
      <div className="m-auto w-full max-w-md p-6">
        <EmptyState
          title="Received documents unavailable"
          description={error}
          action={
            <button
              type="button"
              onClick={() => void reload()}
              className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-bold text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#834DFB]"
            >
              Retry
            </button>
          }
        />
      </div>
    );
  } else if (documents.length === 0) {
    stateBody = emptyState ? (
      <div className="min-w-0 flex-1 p-6">{emptyState}</div>
    ) : (
      <div className="m-auto w-full max-w-md p-6">
        <EmptyState
          icon={<FileText size={24} strokeWidth={2} aria-hidden="true" />}
          title="No received documents to review yet"
          description="Documents appear here once they have been processed."
        />
      </div>
    );
  }

  const selectedProgress = selectedDoc ? documentProgress(selectedDoc, actions) : null;
  const selectedPercent =
    !selectedProgress || selectedProgress.total === 0
      ? 0
      : Math.round(
          ((readOnly ? selectedProgress.total : selectedProgress.reviewed) /
            selectedProgress.total) *
            100
        );

  return (
    <div
      data-testid="review-mode"
      className="-m-6 flex min-w-0 bg-[#f7f6fb]"
      style={{ height: `calc(100vh - ${TOP_BAR_HEIGHT_PX}px)` }}
    >
      {listInRail && railSlot
        ? createPortal(
            <section aria-label="Received documents" data-testid="review-doc-list">
              {documentList}
            </section>,
            railSlot
          )
        : null}

      {stateBody ? (
        <div className="flex min-h-0 min-w-0 flex-1 overflow-y-auto">{stateBody}</div>
      ) : (
        <>
          {!listInRail ? (
            <aside
              aria-label="Received documents"
              className="h-full w-[200px] shrink-0 overflow-y-auto border-r border-gray-200 bg-white"
            >
              {documentList}
            </aside>
          ) : null}

          <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col">
            <p
              data-testid="viewer-caption"
              className="shrink-0 truncate border-b border-gray-200 bg-[#f7f6fb] px-4 py-2 text-[11px] font-bold uppercase tracking-[0.1em] text-gray-500"
            >
              {viewerCaption}
            </p>
            <DocumentViewer
              source={source}
              loading={pdfLoading}
              error={pdfError}
              page={page}
              onPageChange={setPage}
              highlights={highlights}
              selectedId={selectedFieldId}
              onSelect={(id) => {
                const field = selectedDoc?.fields.find((f) => f.id === id);
                if (field) selectField(field);
              }}
            />
          </div>

          <section
            aria-label="Extracted fields"
            className="flex h-full w-[400px] shrink-0 flex-col border-l border-gray-200 bg-white"
          >
            <header className="shrink-0 border-b border-gray-200 px-4 py-3">
              {onBack && !readOnly ? (
                <button
                  type="button"
                  data-testid="back-to-upload"
                  onClick={onBack}
                  title="Return to the upload view to add more documents. Your review so far is kept."
                  className="mb-2 inline-flex items-center gap-1 rounded text-[13px] font-bold text-[#5A21C9] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#834DFB]"
                >
                  <ArrowLeft size={14} strokeWidth={2} aria-hidden="true" />
                  Back to upload
                </button>
              ) : null}
              <div className="flex items-baseline justify-between gap-2">
                <h2
                  className="truncate text-[15px] font-bold text-gray-900"
                  title={`Extracted fields · ${selectedDoc?.documentClass ?? ''}`}
                >
                  Extracted fields{selectedDoc ? ` · ${selectedDoc.documentClass}` : ''}
                </h2>
                {selectedProgress ? (
                  <span
                    data-testid="document-field-count"
                    aria-live="polite"
                    className="shrink-0 text-sm font-bold text-gray-700"
                  >
                    {readOnly
                      ? `${selectedProgress.total} ${selectedProgress.total === 1 ? 'field' : 'fields'}`
                      : `${selectedProgress.reviewed} of ${selectedProgress.total} reviewed`}
                  </span>
                ) : null}
              </div>
              <div
                role="progressbar"
                aria-label="Fields reviewed"
                aria-valuemin={0}
                aria-valuemax={selectedProgress?.total ?? 0}
                aria-valuenow={readOnly ? selectedProgress?.total ?? 0 : selectedProgress?.reviewed ?? 0}
                className="mt-2 h-1.5 overflow-hidden rounded-full bg-gray-100"
              >
                <div className="h-full bg-[#834DFB]" style={{ width: `${selectedPercent}%` }} />
              </div>
              {!readOnly ? (
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    data-testid="accept-all-fields"
                    onClick={handleAcceptEvery}
                    disabled={openCount === 0}
                    title={
                      openCount === 0
                        ? 'Every field in this document already has an action.'
                        : `Accept ${openCount} ${openCount === 1 ? 'field' : 'fields'} that ${openCount === 1 ? 'has' : 'have'} no action, whatever the confidence.`
                    }
                    className="inline-flex items-center gap-1.5 rounded-lg border border-[#d4c6ff] bg-white px-3 py-1.5 text-xs font-bold text-[#5A21C9] transition-colors hover:bg-[#F5F3FF] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#834DFB] focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-white"
                  >
                    <Check size={14} strokeWidth={2} aria-hidden="true" />
                    Accept all
                  </button>
                  <button
                    type="button"
                    data-testid="accept-all"
                    onClick={handleAcceptAll}
                    disabled={bulkCount === 0}
                    title={
                      bulkCount === 0
                        ? `No fields without an action at or above ${THRESHOLD_PERCENT}% confidence.`
                        : `Accept ${bulkCount} ${bulkCount === 1 ? 'field' : 'fields'} at or above ${THRESHOLD_PERCENT}% confidence that ${bulkCount === 1 ? 'has' : 'have'} no action.`
                    }
                    className="inline-flex items-center gap-1.5 rounded-lg border border-[#d4c6ff] bg-white px-3 py-1.5 text-xs font-bold text-[#5A21C9] transition-colors hover:bg-[#F5F3FF] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#834DFB] focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-white"
                  >
                    <Check size={14} strokeWidth={2} aria-hidden="true" />
                    Accept all ≥ {THRESHOLD_PERCENT}%
                  </button>
                </div>
              ) : null}
              {selectedDoc?.errorMessage ? (
                <p role="alert" className="mt-2 text-xs text-[#991b1b]">
                  {selectedDoc.errorMessage}
                </p>
              ) : null}
            </header>

            <div className="min-h-0 flex-1 overflow-y-auto">
              {!selectedDoc || selectedDoc.fields.length === 0 ? (
                <div className="p-4">
                  <EmptyState
                    title="No extracted fields"
                    description="No fields were found in this document. Check that processing finished, then reload."
                  />
                </div>
              ) : (
                <div className="flex flex-col gap-2.5 p-3">
                  {selectedDoc.fields.map((f, index) => {
                    const entry = actions[f.id];
                    return (
                      <div
                        key={f.id}
                        ref={(el) => {
                          cardRefs.current[f.id] = el;
                        }}
                      >
                        <FieldCard
                          variant="review"
                          number={index + 1}
                          label={f.label}
                          value={displayValue(f, actions)}
                          extractedValue={f.value}
                          confidence={f.confidence}
                          page={f.page}
                          selected={f.id === selectedFieldId}
                          onSelect={() => selectField(f)}
                          action={entry?.action ?? null}
                          readOnly={readOnly}
                          onAccept={() =>
                            // Accepting an edited field keeps the corrected value.
                            setActions((prev) =>
                              setFieldAction(prev, f.id, 'accept', prev[f.id]?.editedValue)
                            )
                          }
                          onEdit={
                            readOnly
                              ? // After finalize, Edit is offered only when the caller saves it.
                                onSaveEdit
                                ? (value) => void saveFinalizedEdit(f, value)
                                : undefined
                              : (value) =>
                                  // Saving an edit accepts the field with the corrected value.
                                  setActions((prev) => setFieldAction(prev, f.id, 'accept', value))
                          }
                          threshold={CONFIDENCE_THRESHOLD}
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <footer className="shrink-0 border-t border-gray-200 px-4 py-3">
              {readOnly && editError ? (
                <p role="alert" data-testid="edit-error" className="mb-2 text-xs text-[#991b1b]">
                  {editError}
                </p>
              ) : null}
              {readOnly && savingEdit ? (
                <p role="status" className="mb-2 text-xs text-gray-500">
                  Saving edit…
                </p>
              ) : null}
              {!readOnly && finalizeError ? (
                <p role="alert" className="mb-2 text-xs text-[#991b1b]">
                  {finalizeError}
                </p>
              ) : null}
              <div className="mb-2 flex items-center gap-3">
                <p
                  data-testid="review-progress-text"
                  aria-live="polite"
                  className="shrink-0 text-sm font-bold text-gray-900"
                >
                  {reviewedDocuments} of {totalDocuments}{' '}
                  {totalDocuments === 1 ? 'document' : 'documents'} reviewed
                </p>
                <div
                  role="progressbar"
                  aria-label="Documents reviewed"
                  aria-valuemin={0}
                  aria-valuemax={totalDocuments}
                  aria-valuenow={reviewedDocuments}
                  className="h-1.5 min-w-0 flex-1 overflow-hidden rounded-full bg-gray-100"
                >
                  <div className="h-full bg-[#834DFB]" style={{ width: `${progressPercent}%` }} />
                </div>
              </div>
              <div className="flex items-center justify-between gap-3">
                <p
                  className="min-w-0 text-xs text-gray-500"
                  title={readOnly ? finalizedHint : finalizeHint}
                >
                  {readOnly ? finalizedHint : finalizeHint}
                </p>
                {readOnly ? (
                  <span
                    data-testid="review-finalized"
                    className="inline-flex min-w-[150px] shrink-0 items-center justify-center gap-2 rounded-lg bg-[#dcfce7] px-4 py-2 text-sm font-bold text-[#166534]"
                  >
                    <CheckCircle size={14} strokeWidth={2} aria-hidden="true" />
                    Review finalized
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={handleFinalize}
                    disabled={!canFinalize}
                    title={finalizeEnabled && processingFiles === 0 ? undefined : finalizeBlockedReason}
                    className={FINALIZE_BUTTON}
                  >
                    {finalizing ? (
                      <>
                        <Loader size={14} strokeWidth={2} className="animate-spin" aria-hidden="true" />
                        Finalizing
                      </>
                    ) : (
                      <>
                        <CheckCircle size={14} strokeWidth={2} aria-hidden="true" />
                        Finalize review
                      </>
                    )}
                  </button>
                )}
              </div>
            </footer>
          </section>
        </>
      )}
    </div>
  );
};

export default ReviewMode;
